/**
 * Line types for spatial annotations.
 * Stored in task.taskType — used to determine rendering style.
 */
export const lineTypes = {
  freeLine: 'freeLine',
  freeArrow: 'freeArrow',
  straightLine: 'straightLine',
  straightArrow: 'straightArrow',
} as const;

export type LineType = (typeof lineTypes)[keyof typeof lineTypes];

export function isLineType(value: string): value is LineType {
  return Object.values(lineTypes).includes(value as LineType);
}

// ── SVG path generation ─────────────────────────────────────────

type Point = [number, number];

function lineVector(a: Point, b: Point) {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  return { length: Math.sqrt(dx * dx + dy * dy), angle: Math.atan2(dy, dx) };
}

const SMOOTHING = 0.1;

function controlPoint(current: Point, previous: Point | undefined, next: Point | undefined, reverse?: boolean): Point {
  const p = previous ?? current;
  const n = next ?? current;
  const o = lineVector(p, n);
  const angle = o.angle + (reverse ? Math.PI : 0);
  const length = o.length * SMOOTHING;
  return [current[0] + Math.cos(angle) * length, current[1] + Math.sin(angle) * length];
}

function bezierCommand(point: Point, i: number, a: Point[]): string {
  const [cpsX, cpsY] = controlPoint(a[i - 1], a[i - 2], point);
  const [cpeX, cpeY] = controlPoint(point, a[i - 1], a[i + 1], true);
  return `C ${cpsX},${cpsY} ${cpeX},${cpeY} ${point[0]},${point[1]}`;
}

function lineCommand(point: Point): string {
  return `L ${point[0]} ${point[1]}`;
}

function svgPath(points: Point[], command: (p: Point, i: number, a: Point[]) => string): string {
  return points.reduce(
    (acc, point, i, a) => (i === 0 ? `M ${point[0]},${point[1]}` : `${acc} ${command(point, i, a)}`),
    '',
  );
}

/**
 * Generate SVG path `d` attribute for a line/arrow.
 * Free types use smooth Bézier curves; straight types use a direct line.
 */
export function getLinePath(coords: Point[], type: LineType): string {
  if (!coords.length) return '';
  const isStraight = type === lineTypes.straightLine || type === lineTypes.straightArrow;
  const points = isStraight ? [coords[0], coords[coords.length - 1]] : coords;
  const command = isStraight ? lineCommand : bezierCommand;
  return svgPath(points, command);
}

/**
 * Generate SVG polygon points for an arrowhead at the end of a line.
 * Returns null for non-arrow types.
 */
export function getArrowHeadPoints(coords: Point[], type: LineType): string | null {
  if (type !== lineTypes.freeArrow && type !== lineTypes.straightArrow) return null;
  if (coords.length < 2) return null;

  const endPoint = coords[coords.length - 1];
  const startPoint = type === lineTypes.freeArrow
    ? coords[Math.floor(coords.length * 0.8)]
    : coords[0];

  const HEADLEN = 20;
  const angle = Math.atan2(endPoint[1] - startPoint[1], endPoint[0] - startPoint[0]);

  const v1x = endPoint[0] - HEADLEN * Math.cos(angle - Math.PI / 6);
  const v1y = endPoint[1] - HEADLEN * Math.sin(angle - Math.PI / 6);
  const v2x = endPoint[0] - HEADLEN * Math.cos(angle + Math.PI / 6);
  const v2y = endPoint[1] - HEADLEN * Math.sin(angle + Math.PI / 6);

  return `${endPoint[0]},${endPoint[1]} ${v1x},${v1y} ${v2x},${v2y}`;
}
